/**
 * Authors: mariushirjanu
 * Created: 19/11/2020
 */

import * as _ from 'underscore';
import { Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { FormlyInputType, FormlyType, GaFormlyService, IExtraFieldConfigurationObject, IFormlyCollapsibleFields } from '@app/shared/formly/ga-formly.service';
import { IWebshopBillingData, WebshopBillingDataService } from './webshop-billing-data.service';
import { FormattingService } from '@app/shared/services/formatting.service';
import { IExternalPartnerRelation, PartnerRelationType, WebshopService } from '../webshop.service';
import { GA1Service } from '@app/shared/ga1/ga1.service';
import { IFileUploadData } from '@app/shared/file-upload/file-upload.service';
import { EVENT_CLOSE_DIALOG, GaEventService, IGAEvent } from '@app/shared/services/ga-event.service';
import { BillingDataService, BILLINGTYPE_CENTRAL, PAYMENTTYPE_CC, PAYMENTTYPE_PO, PROCUREMENTTYPE_BUY, PROCUREMENTTYPE_LEASE } from '@app/shared/services/billing/billing-data.service';
import { CurrencyService } from '@app/shared/services/currency.service';
import { IContact, ILocationExtended, IObject, IValueObject, IWebshop } from '@type_definitions/datatypes';
import { DAY_REGEXP, GaValidateService, IValidatingComponent } from '@app/shared/formly/ga-validate.service';
import { DialogSize, GaMessageService, IDialog } from '@app/shared/message/ga-message.service';
import { AuthService } from '@app/shared/services/auth/auth.service';
import { STATE_BROWSELIST, STATE_CHART_CUSTOMER, STATE_HOME_PAGE, STATE_REPORT } from '@app/shared/app-states';
import { DocumentDataService, DocumentNameType, DocumentOrigin, DocumentTypeCode, IDocumentData } from '@app/shared/document/document-data.service';
import { forkJoin, Observable, Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { util } from '@app/shared/util';
import { WorkflowDataService } from '@app/shared/services/workflow/workflow-data.service';
import { ScrollService } from '@app/shared/scroll/scroll.service';
import { SEC_WS_ADDRESS, SEC_WS_B2B, SEC_WS_BATCHBILLING, SEC_WS_BILLING, SEC_WS_BILLINGCONTRACT, SEC_WS_BILLINGTYPE, SEC_WS_CHARGEBACK, SEC_WS_COMPLIANCE, SEC_WS_CONTRACTTEMPLATE, SEC_WS_LEASING, SEC_WS_MINCLEARINGSTATE, SEC_WS_PAYMENTMODE, SEC_WS_SPENDLIMIT } from '@app/shared/services/auth/security-objects';
import { GaFormlyUtil, IFormlySecureFields } from '@app/shared/formly/ga-formly.util';
import { CHARGETYPE_GLOBAL, CHARGETYPE_LOCAL, IChargebackMargin, MarginsDataService } from '@app/shared/services/margins/margins-data.service';
import { TranslationService } from '@app/shared/translation/translation.service';
import { ButtonAddonComponent } from '@app/shared/formly/addons/button-addon.component';
import { FormlyAlertComponent } from '@app/shared/formly/components/formly-alert.component';
import { Alert } from '@app/shared/app-styles';
import { StateService } from '@uirouter/angular';
import { GaWizard } from '@app/shared/wizard/ga-wizard';
import { GaWizardService } from '@app/shared/wizard/ga-wizard.service';
import { WIZARD_WEBSHOP } from '@app/shared/app-wizards';
import { FAQ_BATCH_BILLING, FAQ_BILLING_TYPE, FAQ_CHARGEBACK, FAQ_ELECTRONIC_INVOICE, FAQ_INVOICE_ADDR_MANAGEMENT, FAQ_INVOICE_COMPLIANCE, FAQ_INVOICE_REPORTS, FAQ_LEASING, FAQ_LEASING_WEBSHOPS_SETTINGS, FAQ_PAYMENT_TYPE, FAQ_SPEND_DASHBOARD } from '@app/shared/faq/faq-codes';
import { GaFormlyValidatorService } from '@app/shared/formly/ga-formly-validator.service';
import { URL_WEBSHOP_SUPPLIER_ACTIVATION_FORM } from '@app/shared/browser/url-collections';
import { FaqLinksComponent } from '@app/shared/faq/faq-links.component';
import { GaStateService } from '@app/shared/services/ga-state.service';
import { ReportParameterCode, ReportParameterShortCode } from '@app/main/report/report-state.util';

const SAVE_ACTION:string = 'save';

interface ICollapsibleSection {
  key:string;
  fields:IFormlyCollapsibleFields;
  faqCode?:string;
  collapsed:boolean;
  showSection?:boolean;
  category?:string;
  screen?:string;
}

interface IFormClass {
  leftColumn1:string;
  leftColumn2:string;
  leftColumn3:string;
  rightColumn:string;
  halfColumn?:string;
  fullColumn?:string;
}

enum WebshopBillingSection {
  BILLING_TYPE = 'BILLING_TYPE',
  INVOICE_ADDRESS_MANAGEMENT = 'INVOICE_ADDRESS_MANAGEMENT',
  INVOICE_COMPLIANCE = 'INVOICE_COMPLIANCE',
  PAYMENT_TERMS = 'PAYMENT_TERMS',
  PAYMENT_TYPE = 'PAYMENT_TYPE',
  LEASING = 'LEASING',
  ELECTRONIC_B2B_INVOICING = 'ELECTRONIC_INVOICING',
  CHARGEBACK_SERVICE = 'CHARGEBACK_SERVICE',
  INVOICE_REPORTS = 'INVOICE_REPORTS',
  SPEND_LIMIT = 'SPEND_LIMIT',
  SPEND_DASHBOARD = 'SPEND_DASHBOARD',
  ORDER_NOTIF_DOC_CONFIG = 'ORDER_NOTIF_DOC_CONFIG'
}

@Component({
  selector: 'webshop-billing',
  template: `
    <div class="webshop-billing">
      <h2>
        <text key="HEADING"></text>
      </h2>
      <div class="row" *ngIf="!loading">
        <!-- categories -->
        <div class="col-md-6" *ngFor="let column of formSections"> <!-- repeat the columns -->
          <div class="col-space-bottom" *ngFor="let section of column"> <!-- repeat the sections -->
            <div *ngIf="section.showSection || section.showSection == undefined">
              <editable-text [key]="section.key" [category]="section.category" [screen]="section.screen" menu="title" highlight="h3 a:eq(0)"></editable-text>
              <collapsible-pane class="pane-detail" title="{{ 'WEBSHOP.BILLING.' + section.key + '.TITLE' | translate }}" [collapsed]="section.collapsed" (onChange)="onSectionCollapsed(section, $event)">
                <div class="form-horizontal collapse-section"> <!-- expanded fields --> 
                  <p *ngIf="showFaqs && section.faqCode">
                    <faq-item [faqCode]="section.faqCode" [noQuestion]="true" [noConfluenceText]="true"></faq-item>
                  </p>
                  <formly [form]="form" [fields]="section.fields.expanded" [model]="model"></formly>
                </div>
              </collapsible-pane>
              <collapsible-pane class="pane-summary" [collapsed]="!section.collapsed">
                <div *ngIf="section.collapsed" class="form-horizontal collapse-section"> <!-- collapsed fields -->
                  <formly [form]="form" [fields]="section.fields.collapsed" [model]="model"></formly>
                </div>
              </collapsible-pane>
            </div>
          </div>
        </div>
      </div>
      <h3 *ngIf="loading">
        <activity-indicator useDefaultText="true"></activity-indicator>
      </h3>
    </div>
  `,
  styleUrls: [ './webshop-billing.scss' ],
  encapsulation: ViewEncapsulation.None,
  providers: [
    GaFormlyService
  ]
})
export class WebshopBillingComponent implements OnInit, OnDestroy, IValidatingComponent {
  @Input() public menu:string;

  public formSections:ICollapsibleSection[][];
  public form:UntypedFormGroup = new UntypedFormGroup({});
  public model:IWebshopBillingData;
  private invoiceAllowLAMAddress:boolean;
  public loading:boolean;

  private wizard:GaWizard;
  private eventHandler:Subscription;
  private createInvoiceAddressDialog:IDialog;
  private activateChargebackDialog:IDialog;
  private readonly webshopId:number;
  private webshop:IWebshop;
  private invoiceAddressSelectField:IExtraFieldConfigurationObject;
  private contactSelectField:IExtraFieldConfigurationObject;
  private ccStatusClearingField:IExtraFieldConfigurationObject;
  private invoiceAddressesLinkField:IExtraFieldConfigurationObject;
  private spendLimitStartDateField:IExtraFieldConfigurationObject;
  private batchBillingCheckboxField:IExtraFieldConfigurationObject;
  private downloadLeasingContractField:IExtraFieldConfigurationObject;
  private documents:IObject;
  public showFaqs:boolean;

  private hasWritePermission:boolean;
  private canUpdateBillingType:boolean;
  private canViewBillingType:boolean;
  private canViewContractTemplate:boolean;
  private canUploadSignedContract:boolean;
  private canUpdateAddress:boolean;
  private canViewAddress:boolean;
  private canUpdateInvoiceCompliance:boolean;
  private canViewInvoiceCompliance:boolean;
  private canUpdatePaymentType:boolean;
  private canViewPaymentType:boolean;
  private canUpdateMinClearingStatus:boolean;
  private canViewMinClearingStatus:boolean;
  private canUpdateLeasing:boolean;
  private canViewLeasing:boolean;
  private canUpdateB2B:boolean;
  private canViewB2B:boolean;
  private canUpdateChargeback:boolean;
  private canViewChargeback:boolean;
  private canUpdateBatchBilling:boolean;
  private canViewBatchBilling:boolean;
  private canUpdateSpendLimit:boolean;
  private canViewSpendLimit:boolean;
  public isACSLUser:boolean;

  private readonly labelClass:IFormClass = {
    leftColumn1: 'col-lg-5 col-md-6',
    leftColumn2: 'col-lg-6 col-md-7',
    leftColumn3: 'col-md-5',
    rightColumn: 'col-lg-5 col-md-5'
  };

  private readonly valueClass:IFormClass = {
    leftColumn1: 'col-lg-7 col-md-6',
    leftColumn2: 'col-lg-6 col-md-5',
    leftColumn3: 'col-md-7',
    rightColumn: 'col-lg-7 col-md-7',
    halfColumn: 'col-lg-4 col-md-6',
    fullColumn: 'col-md-12'
  };

  constructor(
    private authService:AuthService,
    private billingDataService:BillingDataService,
    private currencyService:CurrencyService,
    private documentDataService:DocumentDataService,
    private formlyService:GaFormlyService,
    private formlyValidatorService: GaFormlyValidatorService,
    private formattingService:FormattingService,
    private ga1Service:GA1Service,
    private gaEventService:GaEventService,
    private gaFormlyUtil:GaFormlyUtil,
    private messageService:GaMessageService,
    private gaValidateService:GaValidateService,
    private marginsDataService:MarginsDataService,
    private scrollService:ScrollService,
    private stateService:StateService,
    private translationService:TranslationService,
    private webshopBillingDataService:WebshopBillingDataService,
    private webshopService:WebshopService,
    private wizardService:GaWizardService,
    private workflowDataService:WorkflowDataService,
    private gaStateService:GaStateService
  ) {
    this.wizard = wizardService.getInstance(WIZARD_WEBSHOP);
    this.webshopId = this.webshopService.getWebshopId();
    this.isACSLUser = this.authService.checkAcslUser();
  }

  public ngOnInit():void {
    this.gaStateService.screen = 'WEBSHOP.BILLING';
    // get webshop billing permission
    this.getPermissions();
    this.showFaqs = true;

    // register wizard actions and validator
    this.initWizardSetup();

    // initial webshop billing data
    this.loading = true;


    const dataLoader:Observable<[ IWebshopBillingData, IDocumentData[][], IWebshop ]> = forkJoin([
      this.webshopBillingDataService.getWebshopBillingData(this.webshopService.getWebshopId()),
      this.getDocuments(),
      this.webshopService.getWebshopPromise()
    ])
      .pipe(finalize(() => {
        this.loading = false;
      }));

    dataLoader.subscribe(util.spread((data:IWebshopBillingData, documents:IDocumentData[][], webshop:IWebshop) => {
      this.documents = this.processDocuments(documents);
      this.webshop = webshop;

      // init formly model
      this.model = data;
      this.invoiceAllowLAMAddress = data.invoiceAllowLAMAddress;

      this.initSections();
      this.fillFields();

      this.listen();
    }));
  }

  private getPermissions():void {
    this.hasWritePermission = this.authService.viewWrite(SEC_WS_BILLING);

    this.canUpdateBillingType = this.authService.viewWrite(SEC_WS_BILLINGTYPE);
    this.canViewBillingType = this.authService.hasViewPermission(SEC_WS_BILLINGTYPE);

    this.canViewContractTemplate = this.authService.hasViewPermission(SEC_WS_CONTRACTTEMPLATE);

    this.canUploadSignedContract = this.authService.viewWrite(SEC_WS_BILLINGCONTRACT);

    this.canUpdateAddress = this.authService.viewWrite(SEC_WS_ADDRESS);
    this.canViewAddress = this.authService.hasViewPermission(SEC_WS_ADDRESS);

    this.canUpdateInvoiceCompliance = this.authService.viewWrite(SEC_WS_COMPLIANCE);
    this.canViewInvoiceCompliance = this.authService.hasViewPermission(SEC_WS_COMPLIANCE);

    this.canUpdatePaymentType = this.authService.viewWrite(SEC_WS_PAYMENTMODE);
    this.canViewPaymentType = this.authService.hasViewPermission(SEC_WS_PAYMENTMODE);

    this.canUpdateMinClearingStatus = this.authService.viewWrite(SEC_WS_MINCLEARINGSTATE);
    this.canViewMinClearingStatus = this.authService.hasViewPermission(SEC_WS_MINCLEARINGSTATE);

    this.canUpdateLeasing = this.authService.viewWrite(SEC_WS_LEASING);
    this.canViewLeasing = this.authService.hasViewPermission(SEC_WS_LEASING);

    this.canUpdateB2B = this.authService.viewWrite(SEC_WS_B2B);
    this.canViewB2B = this.authService.hasViewPermission(SEC_WS_B2B);

    this.canUpdateChargeback = this.authService.viewWrite(SEC_WS_CHARGEBACK);
    this.canViewChargeback = this.authService.hasViewPermission(SEC_WS_CHARGEBACK);

    this.canUpdateBatchBilling = this.authService.viewWrite(SEC_WS_BATCHBILLING);
    this.canViewBatchBilling = this.authService.hasViewPermission(SEC_WS_BATCHBILLING);

    this.canUpdateSpendLimit = this.authService.viewWrite(SEC_WS_SPENDLIMIT);
    this.canViewSpendLimit = this.authService.hasViewPermission(SEC_WS_SPENDLIMIT);
  }

  private initWizardSetup():void {
    if (this.hasWritePermission) {
      this.wizard.addAction(
        this.wizardService.createActionButton(
          SAVE_ACTION,
          this.translationService.instant('GA.BUTTONS.SAVE_LABEL', { label: 'HEADING' }),
          'btn btn-success',
          this.saveWebshopBilling
        )
      );
    }

    // register current validator to use validateCurrent() when saving
    this.wizard.currentValidator(this.validate, true);
  }

  private initSections():void {
    this.formSections =
      [
        // left column
        [
          {
            key: WebshopBillingSection.BILLING_TYPE,
            fields: this.createBillingTypeFields(),
            faqCode: FAQ_BILLING_TYPE,
            collapsed: true,
            showSection: this.canViewBillingType
          },
          {
            key: WebshopBillingSection.INVOICE_ADDRESS_MANAGEMENT,
            fields: this.createInvoiceAddressManagementFields(),
            faqCode: FAQ_INVOICE_ADDR_MANAGEMENT,
            collapsed: true,
            showSection: this.canViewAddress
          },
          {
            key: WebshopBillingSection.INVOICE_COMPLIANCE,
            fields: this.createInvoiceComplianceFields(),
            faqCode: FAQ_INVOICE_COMPLIANCE,
            collapsed: true,
            showSection: this.canViewInvoiceCompliance
          },
          {
            key: WebshopBillingSection.PAYMENT_TERMS,
            fields: this.createBatchBillingFields(),
            faqCode: FAQ_BATCH_BILLING,
            collapsed: true,
            showSection: this.canViewBatchBilling
          },
          {
            key: WebshopBillingSection.PAYMENT_TYPE,
            fields: this.createPaymentTypeFields(),
            faqCode: FAQ_PAYMENT_TYPE,
            collapsed: true,
            showSection: this.canViewPaymentType || this.canViewMinClearingStatus
          }
        ],

        // right column
        [
          {
            key: WebshopBillingSection.LEASING,
            fields: this.createLeasingFields(),
            faqCode: FAQ_LEASING,
            collapsed: true,
            showSection: this.canViewLeasing
          },
          {
            key: WebshopBillingSection.ELECTRONIC_B2B_INVOICING,
            fields: this.createB2BInvoicingFields(),
            faqCode: FAQ_ELECTRONIC_INVOICE,
            collapsed: true,
            showSection: this.canViewB2B
          },
          {
            key: WebshopBillingSection.CHARGEBACK_SERVICE,
            fields: this.createChargebackServiceFields(),
            faqCode: FAQ_CHARGEBACK,
            collapsed: true,
            showSection: this.canViewChargeback
          },
          {
            key: WebshopBillingSection.INVOICE_REPORTS,
            fields: this.createInvoiceReportsFields(),
            faqCode: FAQ_INVOICE_REPORTS,
            collapsed: true
          },
          {
           key: WebshopBillingSection.SPEND_LIMIT,
           fields: this.createSpendLimitFields(),
           collapsed: true,
           showSection: this.canViewSpendLimit
          },
          {
            key: WebshopBillingSection.SPEND_DASHBOARD,
            fields: this.createSpendDashboardFields(),
            faqCode: FAQ_SPEND_DASHBOARD,
            collapsed: true
          },
          {
            key: WebshopBillingSection.ORDER_NOTIF_DOC_CONFIG,
            fields: this.createOrderNotificationDocumentsConfigFields(),
            collapsed: true
          }
        ]
      ];
  }

  private listen():void {
    // subscriber to close GA1 dialogs
    this.eventHandler = this.gaEventService.events.subscribe((event:IGAEvent) => {
      if (event.name === EVENT_CLOSE_DIALOG) {
        this.createInvoiceAddressDialog?.hide();
        this.activateChargebackDialog?.hide();

        if (event?.data?.Demrgid) {
          this.marginsDataService.getChargebackMargin(event.data.Demrgid)
            .then((chargebackMargin:IChargebackMargin) => {
              this.model.chargebackMarginType = chargebackMargin.chargebackMarginType;
              this.model.chargebackMargin = chargebackMargin.chargebackMargin;
              this.model.chargebackActivated = true;
            });
        }
      }
    });
  }

  private fillFields():void {
    if (this.canUpdateAddress) {
      this.formlyService.fillField(this.invoiceAddressSelectField, this.webshopBillingDataService.getWebshopInvoiceLocations(this.webshopId));
      if (this.model.centralBillingInvoiceAddress?.id) {
        this.formlyService.fillField(this.contactSelectField, this.webshopBillingDataService.getWebshopInvoiceContacts(this.webshopId, this.model.centralBillingInvoiceAddress.id));
      }
    }

    if (this.canUpdateMinClearingStatus) {
      this.formlyService.fillField(this.ccStatusClearingField, this.workflowDataService.getCCClearingStatuses());
    }
  }

  private getDocuments():Observable<IDocumentData[][]> {
    return forkJoin([
      this.documentDataService.getDocumentsUrls(DocumentOrigin.WEBSHOP, this.webshopId),
      //Contract template is attached to FAQ 494
      this.documentDataService.getDocumentsUrls(DocumentOrigin.FAQ, 494),
      //B2B setup form is attached to FAQ 90
      this.documentDataService.getDocumentsUrls(DocumentOrigin.FAQ, 90)
    ]);
  }

  private processDocuments(documents:IDocumentData[][]):IObject {
    const documentList:IObject = {};

    _.each(documents, (docs:IDocumentData[]) => {
      _.each(docs, (document:IDocumentData) => {
        if (document.documentType === DocumentTypeCode.LEASING_CONTRACT) {
          // if document type is leasing contract we only get the first index (the latest one)
          documentList[<string>document.documentType] = document.attachments?.[0]?.value;
        } else {
          _.each(document.attachments, (attachment:IValueObject) => {
            documentList[ <string>attachment.id ] = attachment.value;
          });
        }
      });
    });

    return documentList;
  }

  private createBillingTypeFields():IFormlyCollapsibleFields {
    const fields:IFormlyCollapsibleFields = {};
    const hideCentralBillingInvoiceTemplateSection = () => !(this.model.defaultBillingType.id === BILLINGTYPE_CENTRAL || this.model.changeBillingType);

    const centralBillingContractLinkField:IExtraFieldConfigurationObject = this.formlyService.aLink('WEBSHOP.BILLING.CENTRAL_BILLING_CONTRACT.TEXT')
      .withExtraOptions({
        templateOptions: {
          href: this.documents[ DocumentNameType.CENTRAL_BILLING_SIGNED_CONTRACT ],
          target: '_blank'
        }
      })
      .withHideExpression(() => !this.model.centralBillingContractId)
      .withoutWrapper()
      .withWrapperClass('margin-top-sm')
      .build();

    const readOnlyFields:IExtraFieldConfigurationObject[] = [
      this.formlyService.aValue('defaultBillingType')
        .withFormat(this.formattingService.formatLov)
        .withLabelClass(this.labelClass.leftColumn1)
        .withValueClass(this.valueClass.leftColumn1)
        .withHideExpression(() => !this.canViewBillingType)
        .build(),

      this.formlyService.aValue('changeBillingType')
        .withFormat(this.formattingService.formatBoolean)
        .withLabelClass(this.labelClass.leftColumn1)
        .withValueClass(this.valueClass.leftColumn1)
        .withHideExpression(() => !this.canViewBillingType)
        .build(),

      this.formlyService.aValue('invoiceCurrency')
        .withLabelClass(this.labelClass.leftColumn1)
        .withValueClass(this.valueClass.halfColumn)
        .withFormat(this.formattingService.formatCurrencyS)
        .withHideExpression(() => hideCentralBillingInvoiceTemplateSection() || !this.canViewAddress)
        .build(),

      this.formlyService.aValue('centralBillingInvoiceAddress')
        .withFormat(this.formattingService.formatLocationHtml)
        .withLabelClass(this.labelClass.leftColumn1)
        .withValueClass(this.valueClass.leftColumn1)
        .withHideExpression(() => !this.model.centralBillingInvoiceAddress || hideCentralBillingInvoiceTemplateSection() || !this.canViewAddress)
        .build(),

      this.formlyService.aValue('centralBillingInvoiceEmailAddress')
        .withEditable('invoiceEmailAddresses')
        .withLabelClass(this.labelClass.leftColumn1)
        .withValueClass(this.valueClass.leftColumn1)
        .withHideExpression(() => hideCentralBillingInvoiceTemplateSection() || !this.canViewAddress)
        .build(),

      this.formlyService.aValue('centralBillingInvoiceContact')
        .withFormat(this.formattingService.formatContactWithPhone)
        .withLabelClass(this.labelClass.leftColumn1)
        .withValueClass(this.valueClass.leftColumn1)
        .withHideExpression(() => hideCentralBillingInvoiceTemplateSection() || !this.canViewAddress)
        .build(),

      this.formlyService.aValue('invoiceInstructions')
        .withLabelClass(this.labelClass.leftColumn1)
        .withValueClass(this.valueClass.leftColumn1)
        .withHideExpression(() => hideCentralBillingInvoiceTemplateSection() || !this.canViewAddress)
        .withMaxLength(1000)
        .build(),

      this.formlyService.aValue('localBillingInvoiceEmailAddress')
        .withEditable('invoiceEmailAddress')
        .withLabelClass(this.labelClass.leftColumn1)
        .withValueClass(this.valueClass.leftColumn1)
        .withHideExpression(() => !this.canViewAddress)
        .build()
    ];

    const writableFields:IExtraFieldConfigurationObject[] = [
      this.formlyService.aSelect('defaultBillingType')
        .isRequired()
        .withLabelClass(this.labelClass.leftColumn1)
        .withValueClass(this.valueClass.halfColumn)
        .withFormat(this.formattingService.formatLov)
        .withOptions(this.billingDataService.getBillingTypes(), true)
        .build(),

      this.formlyService.aCheckbox('changeBillingType')
        .withLabelClass(this.labelClass.leftColumn1)
        .withValueClass(this.valueClass.leftColumn1)
        .build(),

      this.formlyService.aFileUpload('centralBillingContractId', 'WEBSHOP.BILLING.UPLOAD_SIGNED_CONTRACT.TEXT')
        .withUrl(this.authService.authenticatedUrl(`rest/webshops/${this.webshopId}/document?description=ACSL%20signed%20agreement%20for%20central%20billing&docTypeCode=${DocumentTypeCode.CENTRAL_BILLING_CONTRACT}`))
        .withExtraOptions({
          templateOptions: {
            onUploadDone: (uploadedFile:IFileUploadData) => {
              if (uploadedFile?.document?.id) {
                this.model.centralBillingContractId = uploadedFile.document.id;
                centralBillingContractLinkField.templateOptions.url = uploadedFile.document.attachmentUrl;
                centralBillingContractLinkField.hide = false;
              }
            }
          }
        })
        .withLabelClass('d-none')
        .build(),

      this.formlyService.aSelect('invoiceCurrency')
        .withLabelClass(this.labelClass.leftColumn1)
        .withValueClass(this.valueClass.halfColumn)
        .withOptions(this.currencyService.getMainCurrencies())
        .withFormat(this.formattingService.formatCurrencyS)
        .withHideExpression(hideCentralBillingInvoiceTemplateSection)
        .build(),

      this.invoiceAddressSelectField = this.formlyService.aSelect2('centralBillingInvoiceAddress')
        .isRequired()
        .withEditable('billingInvoiceAddress')
        .withLabelClass(this.labelClass.leftColumn1)
        .withValueClass(this.valueClass.leftColumn1)
        .withFormat(this.formattingService.formatLocationHtml)
        .withHideExpression(hideCentralBillingInvoiceTemplateSection)
        .onChange(this.onInvoiceAddressChange)
        .withExtraOptions({
          templateOptions: {
            parseHtml: true
          }
        })
        .build(),

      this.formlyService.anInput('centralBillingInvoiceEmailAddress')
        .withEditable('invoiceEmailAddresses')
        .withLabelClass(this.labelClass.leftColumn1)
        .withValueClass(this.valueClass.leftColumn1)
        .withHideExpression(hideCentralBillingInvoiceTemplateSection)
        .withExtraOptions({
          validators: {
            email: this.formlyValidatorService.multipleEmailValidator
          }
        })
        .build(),

      this.contactSelectField = this.formlyService.aSelect('centralBillingInvoiceContact')
        .withFormat(this.formattingService.formatContact)
        .withLabelClass(this.labelClass.leftColumn1)
        .withValueClass(this.valueClass.leftColumn1)
        .withHideExpression(hideCentralBillingInvoiceTemplateSection)
        .build(),

      this.formlyService.aTextarea('invoiceInstructions')
        .withLabelClass(this.labelClass.leftColumn1)
        .withValueClass(this.valueClass.leftColumn1)
        .withHideExpression(hideCentralBillingInvoiceTemplateSection)
        .build(),

      this.formlyService.anInput('localBillingInvoiceEmailAddress')
        .withEditable('invoiceEmailAddress')
        .withInputType(FormlyInputType.EMAIL)
        .withLabelClass(this.labelClass.leftColumn1)
        .withValueClass(this.valueClass.leftColumn1)
        .build()
    ];

    const {
      roFields,
      wFields,
      sFields
    }:IFormlySecureFields<IWebshopBillingData> = this.gaFormlyUtil.getSecuredFields(readOnlyFields, writableFields, this.canUpdateAddress);

    // ------------------

    fields.collapsed = [
      roFields.defaultBillingType,

      roFields.changeBillingType,

      roFields.centralBillingInvoiceAddress,

      centralBillingContractLinkField
    ];


    fields.expanded = [
      this.canUpdateBillingType? wFields.defaultBillingType : roFields.defaultBillingType,

      this.canUpdateBillingType? wFields.changeBillingType : roFields.changeBillingType,

      this.canViewContractTemplate? this.formlyService.aLink('WEBSHOP.BILLING.DOWNLOAD_CONTRACT_TEMPLATE.TEXT')
        .withExtraOptions({
          templateOptions: {
            href: this.model.invoiceAllowLAMAddress? this.documents[ DocumentNameType.CENTRAL_BILLING_LAM_CONTRACT ] : this.documents[ DocumentNameType.CENTRAL_BILLING_CUSTOMER_CONTRACT ],
            target: '_blank'
          }
        })
        .withLabelClass('d-none')
        .build() : {},

      this.canUploadSignedContract? wFields.centralBillingContractId : {},

      this.canUploadSignedContract? centralBillingContractLinkField : {},

      this.formlyService.aTemplate(`<h4 class="margin-top-lg">${this.translationService.instant('WEBSHOP.BILLING.TEMPLATE_CENTRAL_BILLING.TEXT')}</h4>`)
        .withHideExpression(() => hideCentralBillingInvoiceTemplateSection() || !this.canViewAddress)
        .build(),

      sFields.invoiceCurrency,

      this.formlyService.aTemplate(this.getWarningTemplate('inactiveAddress'))
        .withHideExpression(() => hideCentralBillingInvoiceTemplateSection() || this.model.centralBillingInvoiceAddress?.active || util.isNullOrUndefinedOrEmpty(this.model.centralBillingInvoiceAddress?.active))
        .build(),
      this.canUpdateAddress? this.formlyService.aTemplate(this.getWarningTemplate('notAllowedAddress'))
        .withHideExpression(() => hideCentralBillingInvoiceTemplateSection() || this.model.centralBillingInvoiceAddress?.invoicingAllowed || util.isNullOrUndefinedOrEmpty(this.model.centralBillingInvoiceAddress?.invoicingAllowed) || util.isNullOrUndefinedOrEmpty(this.model.centralBillingInvoiceAddress?.active))
        .build() : {},
      this.canUpdateAddress? this.formlyService.aTemplate(this.getWarningTemplate('notFixedAddress'))
        .withHideExpression(() => hideCentralBillingInvoiceTemplateSection() || !(this.model.invoiceAddressFixedList && !this.model.centralBillingInvoiceAddress?.fromFixedList) || util.isNullOrUndefinedOrEmpty(this.model.centralBillingInvoiceAddress?.fromFixedList))
        .build() : {},
      this.canUpdateAddress? this.formlyService.aTemplate(this.getWarningTemplate('lamAddress'))
        .withHideExpression(() => hideCentralBillingInvoiceTemplateSection() || !(!this.model.invoiceAllowLAMAddress && this.model.centralBillingInvoiceAddress?.lamAddress) || util.isNullOrUndefinedOrEmpty(this.model.centralBillingInvoiceAddress?.lamAddress))
        .build() : {},

      sFields.centralBillingInvoiceAddress,

      this.canUpdateAddress? this.formlyService.aGroup()
        .withLabelClass(this.labelClass.leftColumn1)
        .withValueClass(this.valueClass.leftColumn1)
        .withFields([
          this.formlyService.aValue('centralBillingInvoiceAddress')
            .withoutWrapper()
            .withWrapperClass('col-md-12')
            .withFormat(this.formattingService.formatLocationHtml)
            .build()
        ])
        .withHideExpression(hideCentralBillingInvoiceTemplateSection)
        .build() : {},

      sFields.centralBillingInvoiceEmailAddress,


      this.formlyService.aTemplate(this.getWarningTemplate('inactiveContact'))
        .withHideExpression(() => hideCentralBillingInvoiceTemplateSection() || this.model.centralBillingInvoiceContact?.active || util.isNullOrUndefinedOrEmpty(this.model.centralBillingInvoiceContact?.active))
        .build(),
      this.canUpdateAddress? this.formlyService.aTemplate(this.getWarningTemplate('notAllowedContact'))
        .withHideExpression(() => hideCentralBillingInvoiceTemplateSection() || this.model.centralBillingInvoiceContact?.invoicingAllowed || util.isNullOrUndefinedOrEmpty(this.model.centralBillingInvoiceContact?.invoicingAllowed))
        .build() : {},

      sFields.centralBillingInvoiceContact,


      this.canUpdateAddress? this.formlyService.aValue('centralBillingInvoiceContact')
        .withFormat(this.formattingService.formatContactWithPhone)
        .withLabelClass(this.labelClass.leftColumn1)
        .withValueClass(this.valueClass.leftColumn1)
        .withoutEditable()
        .withEditable('centralBillingInvoiceContactDetails')
        .withHideExpression(() => hideCentralBillingInvoiceTemplateSection() || !this.canViewAddress)
        .build() : {},

      sFields.invoiceInstructions,

      this.formlyService.aTemplate(`<h4 class="margin-top-lg">${this.translationService.instant('WEBSHOP.BILLING.TEMPLATE_LOCAL_BILLING.TEXT')}</h4>`)
        .withHideExpression(() => !this.canViewAddress)
        .build(),

      sFields.localBillingInvoiceEmailAddress
    ];

    return fields;
  }

  private createInvoiceAddressManagementFields():IFormlyCollapsibleFields {
    const fields:IFormlyCollapsibleFields = {};

    this.invoiceAddressesLinkField = this.formlyService.aLink(this.getInvoiceAddressesLinkLabel())
      .withExtraOptions({
        templateOptions: {
          uiSref: STATE_BROWSELIST,
          uiParams: {
            [ReportParameterCode.BROWSELIST_CODE]: 'LOCWEBSHOP',
            [ReportParameterShortCode.FILTERS]: {
              Desprnm: this.webshopId,
              Dealinv_ds: 1
            },
            [ReportParameterShortCode.TITLE]: 'Invoice Addresses',
            [ReportParameterShortCode.SHOW_FILTERS]: false,
            [ReportParameterShortCode.AUTO_LOAD]: true,
            [ReportParameterShortCode.STORED_REPORT]: 'INVOICE_ADDRESSES',
            menu: this.menu
          },
          target: '_blank'
        }
      })
      .withoutWrapper()
      .withHideExpression(() => !this.canViewAddress)
      .build();

    const readOnlyFields:IExtraFieldConfigurationObject[] = [
      this.formlyService.aValue('invoiceAddressFixedList')
        .withFormat(this.formattingService.formatBoolean)
        .withLabelClass(this.labelClass.leftColumn2)
        .withValueClass(this.valueClass.leftColumn2)
        .build(),

      this.formlyService.aValue('invoiceAllowLAMAddress')
        .withFormat(this.formattingService.formatBoolean)
        .withLabelClass(this.labelClass.leftColumn2)
        .withValueClass(this.valueClass.leftColumn2)
        .withEditable('lamInvoiceAddresses')
        .build(),

      this.formlyService.aValue('invoiceAllowSameLocation')
        .withFormat(this.formattingService.formatBoolean)
        .withLabelClass(this.labelClass.leftColumn2)
        .withValueClass(this.valueClass.leftColumn2)
        .build(),

      this.formlyService.aValue('invoiceAllowSearchLocation')
        .withFormat(this.formattingService.formatBoolean)
        .withLabelClass(this.labelClass.leftColumn2)
        .withValueClass(this.valueClass.leftColumn2)
        .build(),

      this.formlyService.aValue('invoiceAllowOneTimeAddress')
        .withFormat(this.formattingService.formatBoolean)
        .withLabelClass(this.labelClass.leftColumn2)
        .withValueClass(this.valueClass.leftColumn2)
        .build()
    ];

    const writableFields:IExtraFieldConfigurationObject[] = [
      this.formlyService.aCheckbox('invoiceAddressFixedList')
        .withLabelClass(this.labelClass.leftColumn2)
        .withValueClass(this.valueClass.leftColumn2)
        .build(),

      this.formlyService.aCheckbox('invoiceAllowLAMAddress')
        .withLabelClass(this.labelClass.leftColumn2)
        .withValueClass(this.valueClass.leftColumn2)
        .withEditable('lamInvoiceAddresses')
        .build(),

      this.formlyService.aCheckbox('invoiceAllowSameLocation')
        .withLabelClass(this.labelClass.leftColumn2)
        .withValueClass(this.valueClass.leftColumn2)
        .build(),

      this.formlyService.aCheckbox('invoiceAllowSearchLocation')
        .withLabelClass(this.labelClass.leftColumn2)
        .withValueClass(this.valueClass.leftColumn2)
        .build(),

      this.formlyService.aCheckbox('invoiceAllowOneTimeAddress')
        .withLabelClass(this.labelClass.leftColumn2)
        .withValueClass(this.valueClass.leftColumn2)
        .build()
    ];

    const {
      roFields,
      sFields
    }:IFormlySecureFields<IWebshopBillingData> = this.gaFormlyUtil.getSecuredFields(readOnlyFields, writableFields, this.canUpdateAddress);

    // ------------------

    fields.collapsed = [
      this.invoiceAddressesLinkField,

      roFields.invoiceAddressFixedList,

      roFields.invoiceAllowLAMAddress,

      roFields.invoiceAllowSameLocation,

      roFields.invoiceAllowSearchLocation,

      roFields.invoiceAllowOneTimeAddress

    ];

    fields.expanded = [
      this.invoiceAddressesLinkField,

      this.canUpdateAddress?
        this.formlyService.aGroup()
          .withoutWrapper()
          .withFields([
            this.formlyService.aLink('WEBSHOP.BILLING.UPLOAD_INVOICE_ADDRESSES.TEXT')
              .withWrapperClass('col col-auto')
              .withExtraOptions({
                templateOptions: {
                  href: this.ga1Service.getGA1Url(GA1Service.URLTYPE_UPLOAD_INVOICE_ADDRESSES, {
                    ejbEvent: 'create',
                    Deimpty: 'LOCATION',
                    Desprid: this.webshopId
                  }, {
                    redirect: true,
                    hideMenu: false,
                    hideTop: false
                  }),
                  target: '_blank',
                  linkClass: 'btn-primary'
                }
              })
              .withoutWrapper()
              .build(),

            this.formlyService.aLink('WEBSHOP.BILLING.CREATE_INVOICE_ADDRESS.TEXT')
              .withoutWrapper()
              .withWrapperClass('col col-auto')
              .onClick(() => {
                const ga1Url:string = this.ga1Service.getGA1Url(GA1Service.URLTYPE_CREATE_LOCATION, {
                  ejbEvent: 'create',
                  Desprid: this.webshopId,
                  browseList: true // needed for closing the dialog
                });
                this.createInvoiceAddressDialog = this.messageService.showGA1Dialog('WEBSHOP.BILLING.CREATE_INVOICE_ADDRESS.TEXT', ga1Url, { size: DialogSize.XL });
              })
              .withExtraOptions({
                templateOptions: {
                  linkClass: 'btn-default'
                }
              })
              .build()
          ])
          .build() : {},

      sFields.invoiceAddressFixedList,

      sFields.invoiceAllowLAMAddress,

      sFields.invoiceAllowSameLocation,

      sFields.invoiceAllowSearchLocation,

      sFields.invoiceAllowOneTimeAddress

    ];

    return fields;
  }

  private createInvoiceComplianceFields():IFormlyCollapsibleFields {
    const fields:IFormlyCollapsibleFields = {};

    const readOnlyFields:IExtraFieldConfigurationObject[] = [
      this.formlyService.aValue('invoiceCompliance')
        .withFormat(this.formattingService.formatBoolean)
        .withLabelClass(this.labelClass.leftColumn2)
        .withValueClass(this.valueClass.leftColumn2)
        .build(),

      this.formlyService.aValue('bpInvoiceMandatory')
        .withFormat(this.formattingService.formatBoolean)
        .withLabelClass(this.labelClass.leftColumn2)
        .withValueClass(this.valueClass.leftColumn2)
        .build(),

      this.formlyService.aValue('invoiceBeforeShips')
        .withFormat(this.formattingService.formatBoolean)
        .withLabelClass(this.labelClass.leftColumn2)
        .withValueClass(this.valueClass.leftColumn2)
        .build()
    ];

    const writableFields:IExtraFieldConfigurationObject[] = [
      this.formlyService.aCheckbox('invoiceCompliance')
        .withLabelClass(this.labelClass.leftColumn2)
        .withValueClass(this.valueClass.leftColumn2)
        .build(),

      this.formlyService.aCheckbox('bpInvoiceMandatory')
        .withLabelClass(this.labelClass.leftColumn2)
        .withValueClass(this.valueClass.leftColumn2)
        .build(),

      this.formlyService.aCheckbox('invoiceBeforeShips')
        .withLabelClass(this.labelClass.leftColumn2)
        .withValueClass(this.valueClass.leftColumn2)
        .build()
    ];

    const {
      roFields,
      sFields
    }:IFormlySecureFields<IWebshopBillingData> = this.gaFormlyUtil.getSecuredFields(readOnlyFields, writableFields, this.canUpdateInvoiceCompliance);

    // ------------------

    fields.collapsed = [
      roFields.invoiceCompliance,

      roFields.bpInvoiceMandatory,

      roFields.invoiceBeforeShips
    ];

    fields.expanded = [
      sFields.invoiceCompliance,

      sFields.bpInvoiceMandatory,

      sFields.invoiceBeforeShips
    ];

    return fields;
  }

  private createPaymentTypeFields():IFormlyCollapsibleFields {
    const fields:IFormlyCollapsibleFields = {};

    const allPaymentOptionsEnabled:Function = () => this.model.paymentTypePO && this.model.paymentTypeCC;

    const defaultPaymentTypeMarkField:IExtraFieldConfigurationObject = this.formlyService.aTemplate('<i class="fa fa-star"></i>')
      .withWrapperClass('col col-auto')
      .build();

    const readOnlyFields:IExtraFieldConfigurationObject[] = [
      // Bank transfer field
      this.formlyService.aValue('paymentTypePO', 'APP.PAYMENT.BANK_TRANSFER')
        .withHideExpression(() => (this.model.defaultPaymentType?.id === PAYMENTTYPE_PO && allPaymentOptionsEnabled()) || !this.canViewPaymentType)
        .withFormat(this.formattingService.formatBoolean)
        .withLabelClass(this.labelClass.leftColumn2)
        .withValueClass(this.valueClass.leftColumn2)
        .build(),

      this.formlyService.aGroup('paymentTypePOGroup')
        .withHideExpression(() => (this.model.defaultPaymentType?.id !== PAYMENTTYPE_PO || !allPaymentOptionsEnabled()) || !this.canViewPaymentType)
        .withWrapperClass('group-with-defaults')
        .withLabelClass('d-none')
        .withValueClass('col-md-12')
        .withFields([
          defaultPaymentTypeMarkField,

          this.formlyService.aValue('paymentTypePO', 'APP.PAYMENT.BANK_TRANSFER')
            .withFormat(this.formattingService.formatBoolean)
            .withWrapperClass('col-md-7 value-icon')
            .withLabelClass('col-md-10 col-lg-9 col-hug-left')
            .withValueClass('col-md-2 col-lg-3')
            .build()
        ])
        .build(),


      // Credit card field
      this.formlyService.aValue('paymentTypeCC', 'APP.PAYMENT.CREDIT_CARD')
        .withHideExpression(() => (this.model.defaultPaymentType?.id === PAYMENTTYPE_CC && allPaymentOptionsEnabled()) || !this.canViewPaymentType)
        .withFormat(this.formattingService.formatBoolean)
        .withoutEditable()
        .withLabelClass(this.labelClass.leftColumn2)
        .withValueClass(this.valueClass.leftColumn2)
        .build(),

      this.formlyService.aGroup('paymentTypeCCGroup')
        .withHideExpression(() => (this.model.defaultPaymentType?.id !== PAYMENTTYPE_CC || !allPaymentOptionsEnabled()) || !this.canViewPaymentType)
        .withWrapperClass('group-with-defaults')
        .withLabelClass('d-none')
        .withValueClass('col-md-12')
        .withFields([
          defaultPaymentTypeMarkField,

          this.formlyService.aValue('paymentTypeCC', 'APP.PAYMENT.CREDIT_CARD')
            .withFormat(this.formattingService.formatBoolean)
            .withWrapperClass('col-md-7 value-icon')
            .withoutEditable()
            .withLabelClass('col-md-10 col-lg-9 col-hug-left')
            .withValueClass('col-md-2 col-lg-3 col-hug-right')
            .build(),

          this.formlyService.aValue('paymentTypeCCPct')
            .withoutWrapper()
            .withWrapperClass('col-md-4')
            .withFormat(this.formattingService.formatPct2)
            .build()
        ])
        .build(),

      this.formlyService.aValue('ccStatusClearing')
        .withFormat(this.formattingService.formatLov)
        .withLabelClass(this.labelClass.leftColumn2)
        .withValueClass(this.valueClass.leftColumn2)
        .withHideExpression(() => !this.model.paymentTypeCC || !this.model.ccStatusClearing || !this.canViewMinClearingStatus)
        .build()
    ];

    const writableFields:IExtraFieldConfigurationObject[] = [
      this.formlyService.aCheckbox('paymentTypePO', 'APP.PAYMENT.BANK_TRANSFER')
        .withLabelClass(this.labelClass.leftColumn2)
        .withValueClass(this.valueClass.leftColumn2)
        .withoutEditable()
        .build(),

      this.formlyService.aSelect('defaultPaymentType')
        .withHideExpression(() => !(this.model.paymentTypePO && this.model.paymentTypeCC))
        .withFormat(this.formattingService.formatLov)
        .withLabelClass(this.labelClass.leftColumn2)
        .withValueClass(this.valueClass.leftColumn2)
        .withOptions(this.billingDataService.getPaymentTypes())
        .build(),

      this.ccStatusClearingField = this.formlyService.aSelect('ccStatusClearing')
        .withFormat(this.formattingService.formatLov)
        .withHideExpression(() => !this.model.paymentTypeCC)
        .withLabelClass(this.labelClass.leftColumn2)
        .withValueClass(this.valueClass.leftColumn2)
        .build()
    ];

    const {
      roFields,
      wFields,
      sFields
    } = this.gaFormlyUtil.getSecuredFields(readOnlyFields, writableFields, this.canUpdatePaymentType);

    // ------------------

    fields.collapsed = [
      roFields.paymentTypePO,
      roFields.paymentTypePOGroup,

      roFields.paymentTypeCC,
      roFields.paymentTypeCCGroup,

      roFields.ccStatusClearing
    ];

    fields.expanded = [
      sFields.paymentTypePO,

      this.canUpdatePaymentType? {} : roFields.paymentTypePOGroup,

      this.canUpdatePaymentType? this.formlyService.aGroup(undefined, 'APP.PAYMENT.CREDIT_CARD')
        .withHideExpression(() => !this.model.paymentTypeCCPct)
        .withLabelClass(this.labelClass.leftColumn2)
        .withValueClass(this.valueClass.leftColumn2)
        .withFields([
          this.formlyService.aCheckbox('paymentTypeCC', `(${this.model.paymentTypeCCPct} %)`)
            .withWrapperClass('col-md-12')
            .withoutWrapper()
            .onChange((newValue:boolean) => {
              if (!newValue) {
                this.model.defaultPaymentType = undefined;
                this.model.ccStatusClearing = undefined;
              }
            })
            .build()
        ])
        .build() : roFields.paymentTypeCC,

      this.canUpdatePaymentType? {} : roFields.paymentTypeCCGroup,

      this.canUpdatePaymentType? wFields.defaultPaymentType : {},

      this.canUpdateMinClearingStatus? wFields.ccStatusClearing : roFields.ccStatusClearing
    ];

    return fields;
  }

  private createLeasingFields():IFormlyCollapsibleFields {
    const fields:IFormlyCollapsibleFields = {};

    const allProcurementTypesEnabled:Function = () => this.model.procurementTypeBuy && this.model.procurementTypeLease;

    const defaultProcurementTypeMarkField:IExtraFieldConfigurationObject = this.formlyService.aTemplate('<i class="fa fa-star"></i>')
      .withWrapperClass('col col-auto')
      .build();

    const supplierActivationUrl:string = this.authService.authenticatedUrl(URL_WEBSHOP_SUPPLIER_ACTIVATION_FORM.replace('{webshopId}', this.webshopId.toString()));

    const readOnlyFields:IExtraFieldConfigurationObject[] = [

      // Procurement BUY field
      this.formlyService.aValue('procurementTypeBuy', 'APP.PAYMENT.BUY')
        .withHideExpression(() => (this.model.defaultProcurementType?.id === PROCUREMENTTYPE_BUY && allProcurementTypesEnabled()))
        .withFormat(this.formattingService.formatBoolean)
        .withLabelClass(this.labelClass.leftColumn2)
        .withValueClass(this.valueClass.leftColumn2)
        .build(),

      this.formlyService.aGroup('procurementTypeBuyGroup')
        .withHideExpression(() => (this.model.defaultProcurementType?.id !== PROCUREMENTTYPE_BUY || !allProcurementTypesEnabled()))
        .withoutWrapper()
        .withWrapperClass('group-with-defaults')
        .withFields([
          defaultProcurementTypeMarkField,

          this.formlyService.aValue('procurementTypeBuy', 'APP.PAYMENT.BUY')
            .withFormat(this.formattingService.formatBoolean)
            .withWrapperClass('col-md-7 value-icon')
            .withLabelClass('col-md-10 col-lg-9 col-hug-left')
            .withValueClass('col-md-2 col-lg-3')
            .build()
        ])
        .build(),


      // Procurement LEASE field
      this.formlyService.aValue('procurementTypeLease', 'APP.PAYMENT.LEASE')
        .withHideExpression(() => (this.model.defaultProcurementType?.id === PROCUREMENTTYPE_LEASE && allProcurementTypesEnabled()))
        .withFormat(this.formattingService.formatBoolean)
        .withoutEditable()
        .withLabelClass(this.labelClass.leftColumn2)
        .withValueClass(this.valueClass.leftColumn2)
        .build(),

      this.formlyService.aGroup('procurementTypeLeaseGroup')
        .withHideExpression(() => (this.model.defaultProcurementType?.id !== PROCUREMENTTYPE_LEASE || !allProcurementTypesEnabled()))
        .withWrapperClass('group-with-defaults')
        .withLabelClass('d-none')
        .withValueClass('col-md-12')
        .withFields([
          defaultProcurementTypeMarkField,

          this.formlyService.aValue('procurementTypeLease', 'APP.PAYMENT.LEASE')
            .withFormat(this.formattingService.formatBoolean)
            .withWrapperClass('col-md-7 value-icon')
            .withoutEditable()
            .withLabelClass('col-md-10 col-lg-9 col-hug-left')
            .withValueClass('col-md-2 col-lg-3 col-hug-right')
            .build()
        ])
        .build(),
      
      // hide prices settings

      this.formlyService.aValue('hideProcurementPriceForCustomer', 'WEBSHOP.BILLING.HIDE_PROCUREMENT_PRICES.TEXT')
        .withFormat(this.formattingService.formatBoolean)
        .withoutEditable()
        .withHideExpression(() => !this.isACSLUser)
        .withLabelClass(this.labelClass.leftColumn2)
        .withValueClass(this.valueClass.leftColumn2)
        .build(),

      this.formlyService.aValue('defaultProcurementType')
        .withLabelClass(this.labelClass.leftColumn2)
        .withValueClass(this.valueClass.leftColumn2)
        .withFormat(this.formattingService.formatLov)
        .build(),

      this.formlyService.aLink('WEBSHOP.BILLING.DOWNLOAD_BP_PROFILES.TEXT')
        .withUrl(supplierActivationUrl)
        .withKey('downloadBPProfile')
        .withLabelClass('d-none')
        .withValueClass(this.valueClass.fullColumn)
        .withGroupClass('no-margin-bottom')
        .withExtraOptions({
          templateOptions: {
            icon: 'fa-download'
          }
        })
        .build(),

      this.formlyService.aValue('lessorRelations')
        .withFormat(this.formatRelationsValue)
        .withLabelClass(this.labelClass.leftColumn2)
        .withValueClass(this.valueClass.leftColumn2)
        .withHideExpression(() => !(this.model.lessorRelations && this.model.lessorRelations?.length > 0))
        .build(),

      this.formlyService.aLink('WEBSHOP.BILLING.MANAGE_LESSORS.TEXT')
        .withKey('manageLessors')
        .withLabelClass(this.labelClass.leftColumn2)
        .withValueClass('d-inline-block')
        .withoutWrapper()
        .withGroupClass('no-margin-bottom')
        .withExtraOptions({
          templateOptions: {
            icon: 'fa-info-circle',
            target: '_blank',
            href: this.generateExternalPartnersRelationLink(PartnerRelationType.LESSOR)
          }
        })
        .build()
    ];

    const writableFields:IExtraFieldConfigurationObject[] = [
      this.formlyService.aCheckbox('procurementTypeBuy', 'APP.PAYMENT.BUY')
        .withLabelClass(this.labelClass.leftColumn2)
        .withValueClass(this.valueClass.leftColumn2)
        .withoutEditable()
        .onChange(() => {
          this.model.defaultProcurementType = undefined;
        })
        .build(),

      this.formlyService.aCheckbox('procurementTypeLease', 'APP.PAYMENT.LEASE')
        .withLabelClass(this.labelClass.leftColumn2)
        .withValueClass(this.valueClass.leftColumn2)
        .withoutEditable()
        .onChange(() => {
          this.model.defaultProcurementType = undefined;
        })
        .build(),

      this.formlyService.aCheckbox('hideProcurementPriceForCustomer', 'WEBSHOP.BILLING.HIDE_PROCUREMENT_PRICES.TEXT')
        .withLabelClass(this.labelClass.leftColumn2)
        .withHideExpression(() => !this.isACSLUser)
        .withValueClass(this.valueClass.leftColumn2)
        .build(),
      this.formlyService.aCustom()
        .withoutWrapper()
        .withKey('showFAQ')
        .withHideExpression(() => !this.isACSLUser)
        .withComponent(FaqLinksComponent)
        .withExtraOptions({
          templateOptions: {
            inputs: {
              faqCodes: [ FAQ_LEASING_WEBSHOPS_SETTINGS ]
            }
          }
        })
        .build(),

      this.formlyService.aSelect('defaultProcurementType')
        .withHideExpression(() => !(this.model.procurementTypeBuy && this.model.procurementTypeLease))
        .withFormat(this.formattingService.formatLov)
        .withLabelClass(this.labelClass.leftColumn2)
        .withValueClass(this.valueClass.leftColumn2)
        .withOptions(this.billingDataService.getProcurementTypes())
        .build(),

      this.formlyService.aFileUpload('leasingContractDocuments', 'WEBSHOP.BILLING.LEASING_CONTRACT_UPLOAD.TEXT')
        .withUrl(this.authService.authenticatedUrl(`rest/webshops/${this.webshopId}/document?description=Leasing%20Contract&docTypeCode=${DocumentTypeCode.LEASING_CONTRACT}`))
        .withExtraOptions({
          templateOptions: {
            onUploadDone: (uploadedFile:IFileUploadData) => {
              const attachmentUrl:string = uploadedFile?.document?.attachmentUrl;
              this.downloadLeasingContractField.templateOptions.url = attachmentUrl;
              this.documents[DocumentTypeCode.LEASING_CONTRACT] = attachmentUrl;
            }
          },
          expressionProperties: {
            'templateOptions.valueClass': () => {
              return this.documents?.[DocumentTypeCode.LEASING_CONTRACT] ? this.valueClass.leftColumn2 : `${this.valueClass.leftColumn2} margin-top-sm`;
            }
          }
        })
        .withHideExpression(() => !this.canUpdateLeasing)
        .withLabelClass('d-none')
        .build(),

      this.downloadLeasingContractField = this.formlyService.aLink('WEBSHOP.BILLING.LEASING_CONTRACT_DOWNLOAD.TEXT')
        .withKey('downloadLeasingContract')
        .withUrl(this.documents?.[DocumentTypeCode.LEASING_CONTRACT])
        .withExtraOptions({
          templateOptions: {
            icon: 'fa-download'
          }
        })
        .withHideExpression(() => !this.documents?.[DocumentTypeCode.LEASING_CONTRACT])
        .withLabelClass('d-none')
        .build(),

      this.formlyService.aLink('WEBSHOP.BILLING.LEASING_ADDRESS.TEXT')
        .withKey('leasingAddress')
        .withUrl(`/ga2/#/reports/40?autoload=true&filters=Desprnm%09${this.webshopId}&filters=Company_type%09L&menu=WEBSHOP_${this.webshopId}`)
        .withExtraOptions({
          templateOptions: {
            icon: 'fa-table',
            target: '_blank'
          }
        })
        .withGroupClass('no-margin-bottom')
        .withLabelClass('d-none')
        .build(),

      this.formlyService.aLink('WEBSHOP.BILLING.BP_LESSOR_ID.TEXT')
        .withKey('bpLessorIds')
        .withUrl(`/ga2/#/reports/311?storedReport=BP_LESSOR_ID_WEBSHOP&autoload=true&filters=Bp_webshop%09${this.webshopId}&filters=Owner_company_type_description%09L&menu=WEBSHOP_${this.webshopId}`)
        .withExtraOptions({
          templateOptions: {
            icon: 'fa-table',
            target: '_blank'
          }
        })
        .withGroupClass('no-margin-bottom')
        .withLabelClass('d-none')
        .build()
    ];

    const {
      roFields,
      wFields,
      sFields
    } = this.gaFormlyUtil.getSecuredFields(readOnlyFields, writableFields, this.canUpdateLeasing);

    // ------------------

    fields.collapsed = [
      roFields.procurementTypeBuy,
      roFields.procurementTypeBuyGroup,

      roFields.procurementTypeLease,
      roFields.procurementTypeLeaseGroup,

      roFields.hideProcurementPriceForCustomer,

      roFields.lessorRelations,
      roFields.manageLessors
    ];

    fields.expanded = [
      sFields.procurementTypeBuy,
      this.canUpdateLeasing? {} : roFields.procurementTypeBuyGroup,

      sFields.procurementTypeLease,
      this.canUpdateLeasing? {} : roFields.procurementTypeLeaseGroup,
      
      sFields.hideProcurementPriceForCustomer,
      sFields.showFAQ,
      this.canUpdateLeasing? wFields.defaultProcurementType : {},

      roFields.lessorRelations,
      roFields.manageLessors,

      sFields.leasingAddress,
      sFields.bpLessorIds,

      roFields.downloadBPProfile,
      sFields.downloadLeasingContract,
      sFields.leasingContractDocuments
    ];

    return fields;
  }

  private createOrderNotificationDocumentsConfigFields():IFormlyCollapsibleFields {
    const fields:IFormlyCollapsibleFields = {};

    const readOnlyFields:IExtraFieldConfigurationObject[] = [
      this.formlyService.aValue('orderSummaryInOrderNotification')
        .withFormat(this.formattingService.formatBoolean)
        .withLabelClass(this.labelClass.leftColumn2)
        .withValueClass(this.valueClass.leftColumn2)
        .build(),

      this.formlyService.aValue('billingStatementInOrderNotification')
        .withFormat(this.formattingService.formatBoolean)
        .withLabelClass(this.labelClass.leftColumn2)
        .withValueClass(this.valueClass.leftColumn2)
        .build()
      ];

    const writableFields:IExtraFieldConfigurationObject[] = [
      this.formlyService.aCheckbox('orderSummaryInOrderNotification')
        .withLabelClass(this.labelClass.leftColumn2)
        .withValueClass(this.valueClass.leftColumn2)
        .build(),

      this.formlyService.aCheckbox('billingStatementInOrderNotification')
        .withLabelClass(this.labelClass.leftColumn2)
        .withValueClass(this.valueClass.leftColumn2)
        .build()
    ];

    const {
      roFields,
      sFields
    }:IFormlySecureFields<IWebshopBillingData> = this.gaFormlyUtil.getSecuredFields(readOnlyFields, writableFields, true);

    fields.collapsed = [
      roFields.orderSummaryInOrderNotification,

      roFields.billingStatementInOrderNotification
    ];

    fields.expanded = [
      sFields.orderSummaryInOrderNotification,

      sFields.billingStatementInOrderNotification
    ];

    return fields;
  }

  private createB2BInvoicingFields():IFormlyCollapsibleFields {
    const fields:IFormlyCollapsibleFields = {};

    const b2bSetupTicketsLinkField:IExtraFieldConfigurationObject = this.formlyService.aLink(this.getB2BSetupTicketsLinkLabel())
      .withExtraOptions({
        templateOptions: {
          uiSref: STATE_REPORT,
          uiParams: {
            [ReportParameterCode.REPORT_ID]: 129,
            [ReportParameterShortCode.FILTERS]: {
              Shmtyds: 'TODO_PR_B2B,TODO_IT_B2B,TODO_CO_B2B',
              Shcomnm: this.webshop.customerPartnerRelation.customerCompany.id
            },
            [ReportParameterShortCode.AUTO_LOAD]: true
          },
          target: '_blank'
        }
      })
      .withHideExpression(() => !this.model.totalB2BSetupTickets)
      .withoutWrapper()
      .build();

    const b2bSetupTicketsTemplateField:IExtraFieldConfigurationObject = this.formlyService.aTemplate(this.translationService.instant('WEBSHOP.BILLING.NO_B2B_SETUP_TICKETS'))
      .withHideExpression(() => !!this.model.totalB2BSetupTickets)
      .build();

    let b2bInvoiceMessagesField:IExtraFieldConfigurationObject;

    if (this.model.totalB2BInvoiceMessages) {
      b2bInvoiceMessagesField = this.formlyService.aLink(this.translationService.instant('WEBSHOP.BILLING.B2B_INVOICE_MESSAGES.TEXT', { total: this.model.totalB2BInvoiceMessages }))
        .withExtraOptions({
          templateOptions: {
            uiSref: STATE_REPORT,
            uiParams: {
              [ReportParameterCode.REPORT_ID]: 66,
              [ReportParameterShortCode.FILTERS]: {
                Desprnm: this.webshopId,
                Demesty: 'InvoiceDetailRequest',
                Dedelst: 'SENT',
                Demesdt_FROM: null
              },
              [ReportParameterShortCode.AUTO_LOAD]: true
            },
            target: '_blank'
          }
        })
        .withoutWrapper()
        .build();
    } else {
      b2bInvoiceMessagesField = this.formlyService.aTemplate(this.translationService.instant('WEBSHOP.BILLING.NO_B2B_INVOICE_MESSAGES'))
        .build();
    }

    const b2bSurveyReadOnlyFields:IExtraFieldConfigurationObject = this.formlyService.aGroup()
      .withLabelClass('d-none')
      .withoutWrapper()
      .withFields([
        this.formlyService.aValue('b2bSurveyQuestion')
          .withoutWrapper()
          .withValueClass(this.labelClass.leftColumn2)
          .withLabelClass('d-none')
          .build(),

        this.formlyService.aValue('b2bSurveyAnswer')
          .withoutWrapper()
          .withValueClass(this.valueClass.leftColumn2)
          .withLabelClass('d-none')
          .build()
      ])
      .withHideExpression(() => !(this.model.b2bSurveyQuestion && this.model.b2bSurveyAnswer))
      .build();

    const invoiceRelationsField: IExtraFieldConfigurationObject = this.formlyService.aValue('invoicePlatformRelations')
      .withFormat(this.formatRelationsValue)
      .withLabelClass(this.labelClass.leftColumn2)
      .withValueClass(this.valueClass.leftColumn2)
      .withHideExpression(() => !(this.model.invoicePlatformRelations && this.model.invoicePlatformRelations?.length > 0))
      .build();

    const manageInvoiceRelationsField: IExtraFieldConfigurationObject = this.formlyService.aLink('WEBSHOP.BILLING.MANAGE_INVOICE_PLATFORMS.TEXT')
      .withLabelClass(this.labelClass.leftColumn2)
      .withValueClass('d-inline-block margin-top-xs')
      .withoutWrapper()
      .withExtraOptions({
        templateOptions: {
          icon: 'fa-info-circle',
          target: '_blank',
          href: this.generateExternalPartnersRelationLink(PartnerRelationType.INVOICING)
        },
        expressionProperties: {
          'templateOptions.valueClass': (model: IWebshopBillingData) => {
            return (model.invoicePlatformRelations && model.invoicePlatformRelations?.length > 0) ? 'd-inline-block' : 'd-inline-block margin-top-xs';
          }
        }
      })
      .build();

    fields.collapsed = [
      b2bSetupTicketsTemplateField,

      b2bSetupTicketsLinkField,

      b2bInvoiceMessagesField,

      b2bSurveyReadOnlyFields,

      invoiceRelationsField,
      manageInvoiceRelationsField
    ];

    invoiceRelationsField.templateOptions.groupClass = 'no-margin-bottom';
    fields.expanded = [
      this.canUpdateB2B? this.formlyService.aLink('WEBSHOP.BILLING.B2B_SETUP_FORM.TEXT')
        .withExtraOptions({
          templateOptions: {
            href: this.documents[ DocumentNameType.CUSTOMER_B2B_QUESTIONNAIRE_FORM ],
            target: '_blank'
          }
        })
        .withLabelClass('d-none')
        .build() : {},

      this.canUpdateB2B? this.formlyService.aFileUpload('b2bCustomerFormId', 'WEBSHOP.BILLING.B2B_UPLOAD_SETUP_FORM.TEXT')
        .withUrl(this.authService.authenticatedUrl(`rest/webshops/${this.webshopId}/document?description=Customer%20B2B%20form&docTypeCode=${DocumentTypeCode.B2B_PROCESS}`))
        .withExtraOptions({
          templateOptions: {
            onUploadDone: (uploadedFile:IFileUploadData) => {
              if (uploadedFile?.document?.id) {
                this.model.totalB2BSetupTickets = uploadedFile?.webshopDocumentContext?.totalB2BSetupTickets;
                b2bSetupTicketsLinkField.templateOptions.label = this.getB2BSetupTicketsLinkLabel();
              }
            }
          }
        })
        .withLabelClass('d-none')
        .build() : {},

      b2bSetupTicketsTemplateField,

      b2bSetupTicketsLinkField,

      b2bInvoiceMessagesField,

      invoiceRelationsField,
      manageInvoiceRelationsField,

      this.canUpdateB2B? this.formlyService.aGroup()
        .withLabelClass('d-none')
        .withoutWrapper()
        .withFields([
          this.formlyService.aValue('b2bSurveyQuestion')
            .withoutWrapper()
            .withValueClass('col-md-12')
            .withLabelClass('d-none')
            .build(),
          this.formlyService.aTextarea('b2bSurveyAnswer')
            .withoutWrapper()
            .withValueClass('col-md-12')
            .withLabelClass('d-none')
            .build()
        ])
        .build() : b2bSurveyReadOnlyFields
    ];

    return fields;
  }

  private createChargebackServiceFields():IFormlyCollapsibleFields {
    const fields:IFormlyCollapsibleFields = {};

    const chargebackGlobalMarginField:IExtraFieldConfigurationObject = this.formlyService.aValue('chargebackMargin')
      .withLabelClass(this.labelClass.leftColumn2)
      .withValueClass(this.valueClass.leftColumn2)
      .withHideExpression(() => !this.model.chargebackActivated || this.model.chargebackMarginType !== CHARGETYPE_GLOBAL)
      .build();

    const chargebackLocalMarginField = this.formlyService.aTemplate(this.translationService.instant('WEBSHOP.BILLING.CHARGEBACK_ACTIVATED_LOCALLY.TEXT'))
      .withHideExpression(() => !this.model.chargebackActivated || this.model.chargebackMarginType !== CHARGETYPE_LOCAL)
      .build();

    const chargebackMarginsLink:IExtraFieldConfigurationObject = this.formlyService.aLink('WEBSHOP.BILLING.CHARGEBACK_MARGINS.TEXT')
      .withExtraOptions({
        templateOptions: {
          href: this.ga1Service.getGA1Url(GA1Service.URLTYPE_WEBSHOP_VIEW_MARGINS, {
            row0_Desprid: this.webshopId,
            Demrgty: 'SUPERCUSTO'
          }, {
            redirect: true,
            hideMenu: false,
            hideTop: false
          }),
          target: '_blank'
        }
      })
      .withHideExpression(() => !this.model.chargebackActivated)
      .withoutWrapper()
      .build();

    const activateChargebackField:IExtraFieldConfigurationObject = this.formlyService.aLink('WEBSHOP.BILLING.ACTIVATE_CHARGEBACK')
      .withoutWrapper()
      .onClick(() => {
        const ga1Url:string = this.ga1Service.getGA1Url(GA1Service.URLTYPE_WEBSHOP_NEW_MARGIN, {
          ejbEvent: 'create',
          row0_Desprid: this.webshopId,
          Demrgty: 'SUPERCUSTO',
          Dechaty: 'RESELLER',
          browseList: true // needed for closing the dialog
        });
        this.activateChargebackDialog = this.messageService.showGA1Dialog('WEBSHOP.BILLING.ACTIVATE_CHARGEBACK', ga1Url);
      })
      .withExtraOptions({
        templateOptions: {
          linkClass: 'btn-primary'
        }
      })
      .withHideExpression(() => this.model.chargebackActivated)
      .build();

    const notActivatedChargebackField = this.formlyService.aTemplate(this.translationService.instant('WEBSHOP.BILLING.CHARGEBACK_NOT_ACTIVATED.TEXT'))
      .withHideExpression(() => this.model.chargebackActivated)
      .build();


    fields.collapsed = [
      chargebackGlobalMarginField,

      chargebackLocalMarginField,

      chargebackMarginsLink,

      notActivatedChargebackField
    ];

    fields.expanded = [
      this.canUpdateChargeback? activateChargebackField : notActivatedChargebackField,

      chargebackGlobalMarginField,

      chargebackLocalMarginField,

      chargebackMarginsLink
    ];


    return fields;
  }

  private createBatchBillingFields():IFormlyCollapsibleFields {
    const fields:IFormlyCollapsibleFields = {};

    const extendedPTFormat = (days: number) => {
      if (days) {
        const ptDays: string = this.formattingService.formatDays(days.toString());
        return this.formattingService.join(
          ptDays,
          `(${this.model?.extendedPaymentTermsPct})`,
          ' '
        );
      }
    };

    const readOnlyFields:IExtraFieldConfigurationObject[] = [
      this.formlyService.aValue('batchBilling')
        .withLabelClass(this.labelClass.leftColumn2)
        .withValueClass(this.valueClass.leftColumn2)
        .withFormat((batchBilling:boolean) => {
          const yesNo:string = this.formattingService.formatBoolean(batchBilling);
          return batchBilling && this.model.batchBillingMargin ? this.formattingService.join(yesNo, `(${this.model.batchBillingMargin})`, ' ') : yesNo;
        })
        .build(),

      this.formlyService.aValue('batchBillingPerCountry')
        .withLabelClass(this.labelClass.leftColumn2)
        .withValueClass(this.valueClass.leftColumn2)
        .withFormat((batchBillingPerCountry:boolean) => {
          return this.formattingService.formatBoolean(batchBillingPerCountry);
        })
        .build(),

      this.formlyService.aValue('batchBillingCutOffDates')
        .withLabelClass(this.labelClass.leftColumn2)
        .withValueClass(this.valueClass.leftColumn2)
        .build(),

      this.formlyService.aValue('centralBillingPaymentTerms')
        .withLabelClass(this.labelClass.leftColumn2)
        .withValueClass(this.valueClass.leftColumn2)
        .withFormat(this.formattingService.formatDays)
        .build(),

      this.formlyService.aValue('batchBillingMaximumPT')
        .withLabelClass(this.labelClass.leftColumn2)
        .withValueClass(this.valueClass.leftColumn2)
        .withFormat(this.formattingService.formatDays)
        .withInfo()
        .showInfo()
        .build(),

      this.formlyService.aValue('extendedPaymentTermsDays')
        .withLabelClass(this.labelClass.leftColumn2)
        .withValueClass(this.valueClass.leftColumn2)
        .withFormat(extendedPTFormat)
        .withHideExpression(() => !this.model?.extendedPaymentTermsDays)
        .build(),

      this.formlyService.aLink('WEBSHOP.BILLING.LOCAL_BILLING_PAYMENT_TERMS')
        .withKey('localBillingPTLink')
        .withoutWrapper()
        .withExtraOptions({
          templateOptions: {
            uiSref: STATE_REPORT,
            uiParams: {
              [ReportParameterCode.REPORT_ID]: 261,
              [ReportParameterShortCode.AUTO_LOAD]: true,
              [ReportParameterShortCode.STORED_REPORT]: 'PAYMENT_TERMS',
              [ReportParameterShortCode.FILTERS]: {
                Desprid: this.webshopId
              },
              menu: `WEBSHOP_${this.webshopId}`
            },
            target: '_blank'
          }
        })
        .withWrapperClass('col-space-top')
        .build()
    ];

    const writableFields:IExtraFieldConfigurationObject[] = [
      this.formlyService.anInput('batchBillingCutOffDates')
        .withLabelClass(this.labelClass.leftColumn2)
        .withValueClass(this.valueClass.halfColumn)
        .withExtraOptions({
          validators: {
            format: {
              expression: (control:UntypedFormControl) => {
                if (control.value) {
                  const days:string[] = control.value?.split(',');
                  const unique:string[] = [];
                  return _.every(days, (day:string) => {
                    unique.push(day);
                    return DAY_REGEXP.test(day);
                  }) && _.unique(unique).length === days.length;
                }
              },
              message: this.translationService.instant('GA.VALIDATION.INVALID_FORMAT', {
                label: 'WEBSHOP.BILLING.BATCHBILLINGCUTOFFDATES.LABEL',
                format: ': day1,day2,day3'
              })
            }
          }
        })
        .build(),

      this.formlyService.anInput('centralBillingPaymentTerms')
        .withLabelClass(this.labelClass.leftColumn2)
        .withValueClass(this.valueClass.halfColumn)
        .withInputType(FormlyInputType.NUMBER)
        .withAddOn('GA.GENERAL.DAYS')
        .withExtraOptions({
          templateOptions: {
            min: 0,
            max: 999
          }
        })
        .build()
    ];

    const {
      roFields,
      sFields
    }:IFormlySecureFields<IWebshopBillingData> = this.gaFormlyUtil.getSecuredFields(readOnlyFields, writableFields, this.canUpdateBatchBilling);

    // ------------------

    fields.collapsed = [
      roFields.batchBilling,

      roFields.batchBillingPerCountry,

      roFields.batchBillingCutOffDates,

      roFields.centralBillingPaymentTerms,

      roFields.batchBillingMaximumPT,

      roFields.extendedPaymentTermsDays,

      roFields.localBillingPTLink
    ];

    fields.expanded = [
      this.canUpdateBatchBilling ?
        this.formlyService.aGroup('batchBilling')
          .withLabelClass(this.labelClass.leftColumn2)
          .withValueClass(this.valueClass.leftColumn2)
          .withFields([
            this.batchBillingCheckboxField = this.formlyService.aCheckbox('batchBilling', this.getBatchBillingCheckboxLabel())
              .withWrapperClass('col-md-12')
              .withoutWrapper()
              .withEditable('batchBillingCheckbox')
              .build()
          ])
          .build() : roFields.batchBilling,

      this.canUpdateBatchBilling ?
        this.formlyService.aCheckbox('batchBillingPerCountry')
          .withLabelClass(this.labelClass.leftColumn2)
          .withValueClass(this.valueClass.leftColumn2)
          .build() : roFields.batchBillingPerCountry,

      sFields.batchBillingCutOffDates,

      sFields.centralBillingPaymentTerms,

      this.formlyService.aCustom()
        .withComponent(FormlyAlertComponent)
        .withExtraOptions({
          templateOptions: {
            inputs: {
              alertClass: `${Alert.WARNING} no-margin exceeded-max-pt`,
              message: this.translationService.instant('WEBSHOP.BILLING.BATCHBILLINGMAXIMUMPT.TEXT', { total: this.model.batchBillingDefaultPT })
            }
          }
        })
        .withLabelClass('d-none')
        .withValueClass('col-md-12')
        .withHideExpression(() => !this.model.defaultPTExceedsMaxPT)
        .build(),

      roFields.batchBillingMaximumPT,

      this.formlyService.aValue('extendedPaymentTermsDays')
        .withLabelClass(this.labelClass.leftColumn2)
        .withValueClass(this.valueClass.leftColumn2)
        .withFormat(extendedPTFormat)
        .withAddOnButton({
          component: ButtonAddonComponent,
          inputs: {
            label: 'GA.BUTTONS.UPDATE',
            buttonClass: 'btn-default',
            title: 'WEBSHOP.BILLING.UPDATE_BUTTON_SOW_TITLE'
          },
          outputs: {
            onClick: () => {
              // Redirect to 'Master SOW' menu
              this.stateService.go(STATE_HOME_PAGE, { url: `/unacc/WP_SOWView_Edit.jsp?Desprid=${this.webshopId}&ejbEvent=create&RealejbEvent=edit` });
            }
          }
        })
        .build(),

      roFields.localBillingPTLink
    ];

    return fields;
  }

  private createInvoiceReportsFields():IFormlyCollapsibleFields {
    const fields:IFormlyCollapsibleFields = {};

    let openInvoicesField:IExtraFieldConfigurationObject;
    if (this.model.totalOpenInvoices) {
      openInvoicesField = this.formlyService.aLink(this.translationService.instant('WEBSHOP.BILLING.OPEN_INVOICES.TEXT', { total: this.model.totalOpenInvoices }))
        .withExtraOptions({
          templateOptions: {
            uiSref: STATE_REPORT,
            uiParams: {
              [ReportParameterCode.REPORT_ID]: 41,
              [ReportParameterShortCode.AUTO_LOAD]: true,
              [ReportParameterShortCode.FILTERS]: {
                Desprnm: this.webshopId,
                Depayst_ds: 0
              }
            },
            target: '_blank'
          }
        })
        .withoutWrapper()
        .build();
    } else {
      openInvoicesField = this.formlyService.aTemplate(this.translationService.instant('WEBSHOP.BILLING.NO_OPEN_INVOICES'))
        .build();
    }

    fields.collapsed = fields.expanded = [
      openInvoicesField,

      this.formlyService.aLink('WEBSHOP.BILLING.INVOICE_HISTORY.TEXT')
        .withExtraOptions({
          templateOptions: {
            uiSref: STATE_REPORT,
            uiParams: {
              [ReportParameterCode.REPORT_ID]: 41,
              [ReportParameterShortCode.SHOW_FILTERS]: true,
              [ReportParameterShortCode.FILTERS]: {
                Desprnm: this.webshopId
              }
            },
            target: '_blank'
          }
        })
        .withoutWrapper()
        .build()
    ];

    return fields;
  }

  private createSpendLimitFields(): IFormlyCollapsibleFields {
    const fields: IFormlyCollapsibleFields = {};
    let collapsedInfoField: IExtraFieldConfigurationObject;

    const getCollapsedInfo = (): string => {
      return `<p class="spend-limit-info">${this.formattingService.formatAmount(this.model.spendLimit, this.model.spendLimitCurrency)}  
        ${this.model.spendLimitStartDate ? `from ${this.formattingService.formatDate(this.model.spendLimitStartDate, 'DD MMM YYYY')}` : ''}`;
    };

    const spendLimitReportLinkField: IExtraFieldConfigurationObject = this.formlyService.aTemplate(`
      <a href="/ga2/#/reports/308?autoload=true&filters=Webshop_name%09${this.webshopId}" target="_blank"><i class="fa fa-table"></i> User spend metrics</a>
    `)
      .build();

    fields.collapsed = [
      collapsedInfoField = this.formlyService.aTemplate(getCollapsedInfo())
        .build(),
      spendLimitReportLinkField
    ];

    fields.expanded = [
      this.formlyService.aField(this.canUpdateSpendLimit ? FormlyType.INPUT : FormlyType.VALUE, 'spendLimit')
        .withLabelClass(this.labelClass.rightColumn)
        .withValueClass(this.valueClass.rightColumn)
        .withInputType('number')
        .withFormat(this.formattingService.formatAmount)
        .withExtraOptions({
          templateOptions: {
            min: 0
          }
        })
        .onChange(() => collapsedInfoField.template = getCollapsedInfo())
        .build(),
      this.formlyService.aField(this.canUpdateSpendLimit ? FormlyType.SELECT : FormlyType.VALUE,'spendLimitCurrency', 'GA.GENERAL.CURRENCY')
        .withLabelClass(this.labelClass.rightColumn)
        .withValueClass(this.valueClass.halfColumn)
        .withLabelProp('value')
        .withValueProp('id')
        .withOptions([
          {
            id: 'EUR',
            value: 'Euro'
          },
          {
            id: 'USD',
            value: 'US Dollar'
          }
        ])
        .onChange(() => collapsedInfoField.template = getCollapsedInfo())
        .build(),
      this.spendLimitStartDateField = this.formlyService.aField(this.canUpdateSpendLimit ? FormlyType.DATE_INPUT : FormlyType.VALUE, 'spendLimitStartDate')
        .withLabelClass(this.labelClass.rightColumn)
        .withValueClass(this.valueClass.rightColumn)
        .withFormat(this.formattingService.formatDate)
        .withAddOnButton(this.canUpdateSpendLimit && {
          component: ButtonAddonComponent,
          inputs: {
            label: 'Reset'
          },
          outputs: {
            onClick: () => {
              this.spendLimitStartDateField.formControl.setValue(new Date());
              this.spendLimitStartDateField.templateOptions.addOnButton.inputs.busy = () => false;
            }
          }
        })
        .onChange(() => collapsedInfoField.template = getCollapsedInfo())
        .build(),
      spendLimitReportLinkField
    ];

    return fields;
  }

  private createSpendDashboardFields():IFormlyCollapsibleFields {
    const fields:IFormlyCollapsibleFields = {};

    fields.collapsed = fields.expanded = [
      this.formlyService.aLink('WEBSHOP.BILLING.CUSTOMER_CHART.TEXT')
        .withExtraOptions({
          templateOptions: {
            uiSref: STATE_CHART_CUSTOMER,
            uiParams: {
              autoload: true,
              webshop: this.webshopId
            },
            target: '_blank'
          }
        })
        .withoutWrapper()
        .build()
    ];
    return fields;
  }

  public onSectionCollapsed(section:ICollapsibleSection, collapsed:boolean):void {
    section.collapsed = collapsed;
  }

  public validate():Promise<string[]> {
    return this.validateFields();
  }

  public validateFields():Promise<string[]> {
    return new Promise((resolve, reject) => {
      let allValidationMessages:string[] = [];
      const promises:Promise<void|string[]>[] = [];
      const sections:ICollapsibleSection[] = [];

      // form validation
      _.each(this.formSections, (column:ICollapsibleSection[]) => {
        _.each(column, (section:ICollapsibleSection, idx:number) => {
          // do not validate hidden fields
          const visibleExpandedFields:IExtraFieldConfigurationObject[] = _.filter(column[ idx ].fields.expanded, (field:IExtraFieldConfigurationObject) => !field.templateOptions.hidden);
          promises.push(
            this.gaValidateService.validateFields(visibleExpandedFields, this.model, true)
          );
          sections.push(section);
        });
      });

      Promise.all(promises)
        .then(() => resolve(undefined))
        .catch((sectionValidationMessages:string[]) => {
          allValidationMessages = allValidationMessages.concat(sectionValidationMessages);
          _.each(sectionValidationMessages, ((sectionValidationMessage:string, idx:number) => {
            if (sectionValidationMessages.length) {
              sections[idx].collapsed = false;
            }
          }));
          reject(allValidationMessages);
        });
    });
  }

  public saveWebshopBilling = ():void => {
    this.validateFields()
      .then(() => {
        const dataToSave:IWebshopBillingData = _.omit(this.model, (value:any) => _.isNull(value)); // omit null values
        delete dataToSave.b2bSurveyQuestion;
        this.webshopBillingDataService.saveWebshopBillingData(this.webshopId, dataToSave)
          .then((response:IWebshopBillingData) => {
            this.model = response;
            // when changing 'Invoice address from fixed list only' the counter has to be refreshed
            this.invoiceAddressesLinkField.templateOptions.label = this.getInvoiceAddressesLinkLabel();
            this.batchBillingCheckboxField.templateOptions.label = this.getBatchBillingCheckboxLabel();
            // when changing invoiceAllowLAMAddress, we have to reload the list of addresses
            if (this.invoiceAllowLAMAddress !== response.invoiceAllowLAMAddress && this.canUpdateAddress) {
              this.formlyService.fillField(this.invoiceAddressSelectField, this.webshopBillingDataService.getWebshopInvoiceLocations(this.webshopId));
              this.invoiceAllowLAMAddress = response.invoiceAllowLAMAddress;
            }
            this.messageService.showFeedbackAfterSave('WEBSHOP.TABS.BILLING');
            if (this.webshopService.onSaved) {
              this.webshopService.onSaved.emit(this.webshopService.getWebshopId());
            }
          });
      })
      .catch(() => {
        setTimeout(() => { // delay for giving some time to formly to execute form validation
          this.scrollService.scrollToElement('.has-error');
        });
      });
  };

  private getWarningTemplate(key:string):string {
    return `
      <div class="margin-top-lg">
        <div class="alert alert-warning warning-${this.formattingService.snakeCase(key)}">
          <i class="fa fa-exclamation-triangle"></i>
          <span translate>${this.translationService.instant('WEBSHOP.BILLING.WARNINGS.' + this.formattingService.snakeCase(key, '_').toUpperCase())}</span>
        </div>
      </div>
    `;
  }

  private onInvoiceAddressChange = (location:ILocationExtended, oldLocation:ILocationExtended):void => {
    const resetInvoiceContact:Function = ():void => {
      this.contactSelectField.templateOptions.initialValue = undefined;
      this.form.controls?.centralBillingInvoiceContact?.reset(undefined, { emitEvent: true });
    };

    // clear the contact when invoice address is changed from one company to another or the contact is not in the list of invoice contacts
    if (location?.company?.id !== oldLocation?.company?.id) {
      resetInvoiceContact();
    }

    this.formlyService.fillField(this.contactSelectField, this.webshopBillingDataService.getWebshopInvoiceContacts(this.webshopId, location.id)
      .then((contacts:IContact[]) => {

        // because of initial contact is not in the list of new contact options, it is necessary to manually clear the contact field
        if (this.model.centralBillingInvoiceContact?.id) {
          const invoiceContactNotFound:boolean = !_.findWhere(contacts, { id: this.model.centralBillingInvoiceContact.id });
          if (invoiceContactNotFound) {
            resetInvoiceContact();
          }
        }

        return contacts;
      }));

  };

  private getInvoiceAddressesLinkLabel = () => this.translationService.instant('WEBSHOP.BILLING.INVOICE_ADDRESSES.TEXT', { total: this.model.totalInvoiceAddresses });
  private getB2BSetupTicketsLinkLabel = () => this.translationService.instant('WEBSHOP.BILLING.B2B_TICKETS.TEXT', { total: this.model.totalB2BSetupTickets });
  private getBatchBillingCheckboxLabel = () => this.model.batchBillingMargin ? `(${this.model.batchBillingMargin})` : '';

  private generateExternalPartnersRelationLink(relationType: PartnerRelationType): string {
    const customerCompanyId: number = this.webshop?.customerPartnerRelation?.customerCompany?.id;
    return `/ga2/#/lists/EXTPREL?autoload=true&filters=Customer_name%09${customerCompanyId}&filters=Relation_type_val%09${relationType}&menu=WEBSHOP_${this.webshopId}`;
  }

  private formatRelationsValue = (relations: IExternalPartnerRelation[]): string => {
    const externalPartners: string[] = _.map(relations, (relation: IExternalPartnerRelation) => {
      return relation.externalPartner?.name;
    });

    return externalPartners.join(', ');
  };

  public ngOnDestroy():void {
    // unsubscribe from events
    this.eventHandler.unsubscribe();
    this.gaStateService.clear();
    this.wizard.clearCurrentValidator();
    this.wizard.removeAction(SAVE_ACTION);
  }
}